import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { summarizeWritingQuality } from "../../../../../shared/index";
import {
  ActionBadge,
  ArticleEvaluationDialog,
  ErrorBlock,
  FiltersBar,
  KpiCards,
  SortableHead,
  StatusBlock,
} from "./components";
import {
  carryFilters,
  formatDate,
  useEvaluatedArticles,
  useSort,
  useWritingQualityFilters,
} from "./hooks";

export default function WriterQualityPage() {
  const { author = "" } = useParams();
  const navigate = useNavigate();
  const { filters, setFilter } = useWritingQualityFilters();
  const query = useEvaluatedArticles(filters);
  const all = useMemo(() => query.data?.articles ?? [], [query.data]);
  const [openId, setOpenId] = useState<string | null>(null);

  const articles = useMemo(
    () =>
      all.filter(
        (a) =>
          a.authorSlug === author && (!filters.action || a.recommendedAction === filters.action)
      ),
    [all, author, filters.action]
  );
  const summary = useMemo(() => summarizeWritingQuality(articles), [articles]);
  const { sorted, sort, toggle } = useSort(articles, "publishedAt", "desc");
  const authorName = all.find((a) => a.authorSlug === author)?.authorName ?? author;
  const backHref = `/dashboard/writing-quality?${carryFilters(filters)}`;

  return (
    <div className="space-y-4">
      <div>
        <Link
          to={backHref}
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> All writers
        </Link>
        <h1 className="text-2xl font-bold">{authorName}</h1>
        <p className="text-sm text-muted-foreground">Writing quality for this writer’s evaluated articles.</p>
      </div>

      <FiltersBar
        filters={{ ...filters, writer: author }}
        setFilter={setFilter}
        articles={all}
        onWriterChange={(slug) =>
          navigate(
            slug
              ? `/dashboard/writing-quality/writers/${encodeURIComponent(slug)}?${carryFilters(filters)}`
              : backHref
          )
        }
      />

      {query.isError && <ErrorBlock error={query.error} fallback="Failed to load evaluations" />}

      <KpiCards summary={summary} showWriters={false} />

      <div className="rounded-lg border bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="font-semibold">Articles {!query.isLoading && `(${articles.length})`}</h2>
          <p className="text-xs text-muted-foreground">Click an article for its full evaluation.</p>
        </div>
        {query.isLoading ? (
          <StatusBlock>
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </StatusBlock>
        ) : articles.length === 0 ? (
          <StatusBlock>No evaluated articles match these filters.</StatusBlock>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead label="Article" sortKey="title" sort={sort} onSort={toggle} />
                  <SortableHead label="Published" sortKey="publishedAt" sort={sort} onSort={toggle} />
                  <SortableHead label="AI-Like Writing" sortKey="aiWriting" sort={sort} onSort={toggle} className="text-right" />
                  <SortableHead label="Editorial Quality" sortKey="editorialQuality" sort={sort} onSort={toggle} className="text-right" />
                  <SortableHead label="Action" sortKey="recommendedAction" sort={sort} onSort={toggle} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((a) => (
                  <TableRow key={a.id} className="cursor-pointer" onClick={() => setOpenId(a.id)}>
                    <TableCell className="max-w-md">
                      <div className="flex items-start gap-1.5">
                        <span className="font-medium">{a.title}</span>
                        {a.url && (
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            title="Open the published article"
                            className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(a.publishedAt)}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.aiWriting}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.editorialQuality}</TableCell>
                    <TableCell>
                      <ActionBadge action={a.recommendedAction} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ArticleEvaluationDialog articleId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
